import React, { useEffect } from "react";
import { X, Bell, ExternalLink } from "lucide-react";
import { Button } from "./ui/Button";

interface MarketingNotificationDialogProps {
  id: string;
  notificationId: string;
  title: string;
  message: string;
  priority?: "low" | "medium" | "high";
  popupData: any;
  onClose: () => void;
}

const MarketingNotificationDialog: React.FC<MarketingNotificationDialogProps> = ({
  id,
  notificationId,
  title,
  message,
  priority = "medium",
  popupData,
  onClose,
}) => {
  // Parse marketing popup data from message
  const parsedPopupData = React.useMemo(() => {
    // If popupData is provided, use it
    if (popupData && typeof popupData === "object") {
      return popupData;
    }
    
    // If popupData is a string, parse it
    if (typeof popupData === "string") {
      try {
        const parsed = JSON.parse(popupData);
        return parsed;
      } catch (e) {
        console.error("Failed to parse popupData string:", e);
      }
    }
    
    // Try to parse from message field
    try {
      const parsed = JSON.parse(message || "{}");
      return parsed;
    } catch (e) {
      console.error("Failed to parse message:", e);
      return {};
    }
  }, [popupData, message]);

  useEffect(() => {
    // Handle escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePrimaryButtonClick = () => {
    if (parsedPopupData.contentPrimaryButtonLink) {
      window.location.href = parsedPopupData.contentPrimaryButtonLink;
    }
    onClose();
  };

  const handleSecondaryButtonClick = () => {
    if (parsedPopupData.contentSecondaryButtonLink) {
      window.location.href = parsedPopupData.contentSecondaryButtonLink;
    }
    onClose();
  };

  const getPriorityColor = () => {
    switch (priority) {
      case "high":
        return "border-red-500 bg-red-50";
      case "low":
        return "border-blue-500 bg-blue-50";
      default:
        return "border-yellow-500 bg-yellow-50";
    }
  };

  const getPriorityLabel = () => {
    switch (priority) {
      case "high":
        return "High Priority";
      case "low":
        return "Low Priority";
      default:
        return "Medium Priority";
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        pointerEvents: "auto",
      }}
      onClick={handleOverlayClick}
    >
      <div
        className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-[95vw] sm:max-w-md md:max-w-lg lg:max-w-xl border-l-4 border-pink-500 transform transition-all mx-auto"
        style={{ 
          textAlign: "left",
          pointerEvents: "auto"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 md:p-6 border-b">
          <div className="flex-shrink-0 mt-0.5">
            <div className="scale-90 sm:scale-100">
              <Bell className="h-5 w-5 sm:h-6 sm:w-6 text-pink-600" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 break-words">
              {parsedPopupData.contentTitle || title || "New Marketing Popup"}
            </h3>
            <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${getPriorityColor()}`}>
              {getPriorityLabel()}
            </span>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-5 md:p-6 max-h-[60vh] sm:max-h-[70vh] overflow-y-auto">
          {(() => {
            const description = parsedPopupData.contentDescription || "Marketing popup content is being loaded...";
            // Auto-detect if content is HTML (starts with HTML tags)
            const startsWithHtmlTag = /^\s*<[^>]+>/.test(description);
            
            if (startsWithHtmlTag) {
              return (
                <>
                  <style dangerouslySetInnerHTML={{
                    __html: `
                      .marketing-dialog-html h1 { font-weight: 700; font-size: clamp(1.3em, 4vw, 1.75em); color: #111827; margin: 0.75em 0 0.5em 0; }
                      .marketing-dialog-html h2 { font-weight: 700; font-size: clamp(1.2em, 3.5vw, 1.5em); color: #111827; margin: 0.75em 0 0.5em 0; }
                      .marketing-dialog-html h3 { font-weight: 700; font-size: clamp(1.1em, 3vw, 1.25em); color: #111827; margin: 0.75em 0 0.5em 0; }
                      .marketing-dialog-html h4 { font-weight: 700; font-size: clamp(1em, 2.5vw, 1.1em); color: #111827; margin: 0.75em 0 0.5em 0; }
                      .marketing-dialog-html p { margin: 0.5em 0; line-height: 1.6; font-size: clamp(0.875rem, 2vw, 1rem); }
                      .marketing-dialog-html ul, .marketing-dialog-html ol { margin: 0.75em 0; padding-left: 1.5em; font-size: clamp(0.875rem, 2vw, 1rem); }
                      .marketing-dialog-html li { margin: 0.375em 0; line-height: 1.6; }
                      .marketing-dialog-html strong { font-weight: 600; color: #374151; }
                      .marketing-dialog-html em { font-style: italic; }
                      .marketing-dialog-html a { color: #3b82f6; text-decoration: underline; word-break: break-word; }
                      .marketing-dialog-html a:hover { color: #2563eb; }
                      .marketing-dialog-html code { background: #f3f4f6; padding: 0.2em 0.4em; border-radius: 3px; font-family: monospace; font-size: 0.85em; }
                    `
                  }} />
                  <div 
                    dangerouslySetInnerHTML={{ __html: description }}
                    className="text-gray-700 leading-relaxed marketing-dialog-html text-sm sm:text-base"
                  />
                </>
              );
            }
            
            return (
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm sm:text-base break-words">
                {description}
              </p>
            );
          })()}

          {/* Debug Info (only show if no content) */}
          {!parsedPopupData.contentTitle && !parsedPopupData.contentDescription && (
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-sm text-gray-500">Debug Info</summary>
              <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-32">
                {JSON.stringify({ popupData, parsedPopupData, message }, null, 2)}
              </pre>
            </details>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 p-3 sm:p-4 md:p-6 border-t bg-gray-50">
          {parsedPopupData.contentPrimaryButtonText && (
            <Button
              onClick={handlePrimaryButtonClick}
              className="flex-1 w-full sm:w-auto text-sm sm:text-base"
              style={{
                backgroundColor: parsedPopupData.designColorsPrimary || "#EC4899",
                color: "#FFFFFF",
              }}
            >
              {parsedPopupData.contentPrimaryButtonText}
            </Button>
          )}
          {parsedPopupData.contentSecondaryButtonText && (
            <Button
              onClick={handleSecondaryButtonClick}
              variant="outline"
              className="flex-1 w-full sm:w-auto text-sm sm:text-base"
              style={{
                borderColor: parsedPopupData.designColorsSecondary || "#EC4899",
                color: parsedPopupData.designColorsSecondary || "#EC4899",
              }}
            >
              {parsedPopupData.contentSecondaryButtonText}
            </Button>
          )}
          {!parsedPopupData.contentPrimaryButtonText && !parsedPopupData.contentSecondaryButtonText && (
            <Button
              variant="outline"
              className="flex-1 w-full sm:w-auto"
              onClick={onClose}
            >
              Close
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketingNotificationDialog;
